import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../services/api';
import { Role, Skill } from '../../types/auth';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { DatePicker } from '../ui/DatePicker';

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    name: '',
    password: '',
    roleId: '',
    isActive: true,
    skillIds: [] as string[],
    patternStartDate: '',
    preferredPatternId: '',
  });
  const [roles, setRoles] = useState<Role[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [patterns, setPatterns] = useState<Array<{ id: string; name: string; daysOn: number; daysOff: number }>>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, rolesRes, skillsRes, patternsRes] = await Promise.all([
          apiClient.get(`/users/${id}`),
          apiClient.get('/roles'),
          apiClient.get('/users/skills/list'),
          apiClient.get('/patterns'),
        ]);

        const user = userRes.data.data;
        setFormData({
          email: user.email,
          username: user.username || '',
          name: user.name,
          password: '',
          roleId: user.role.id,
          isActive: user.isActive,
          skillIds: user.userSkills?.map((us: any) => us.skillId) || [],
          patternStartDate: user.patternStartDate ? new Date(user.patternStartDate).toISOString().split('T')[0] : '',
          preferredPatternId: user.preferredPatternId || '',
        });
        setRoles(rolesRes.data.data);
        setSkills(skillsRes.data.data);
        setPatterns(patternsRes.data.data || patternsRes.data || []);
      } catch (err: any) {
        setError('Failed to fetch user data');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const updateData: any = {
        name: formData.name,
        email: formData.email,
        username: formData.username.trim(),
        roleId: formData.roleId,
        isActive: formData.isActive,
        skillIds: formData.skillIds,
        patternStartDate: formData.patternStartDate ? new Date(formData.patternStartDate) : null,
        preferredPatternId: formData.preferredPatternId || null,
      };

      if (formData.password && formData.password.trim().length > 0) {
        updateData.password = formData.password;
      }

      await apiClient.put(`/users/${id}`, updateData);
      navigate('/admin/users', { replace: true });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error?.details ||
        err.response?.data?.message ||
        'Failed to update user';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkillToggle = (skillId: string) => {
    setFormData((prev) => ({
      ...prev,
      skillIds: prev.skillIds.includes(skillId)
        ? prev.skillIds.filter((id) => id !== skillId)
        : [...prev.skillIds, skillId],
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Edit User</h1>
        <p className="text-slate-500 mt-1">Update account details and permissions</p>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          hint="Users can also update their own email from Settings."
        />

        <Input
          label="Username"
          type="text"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          required
          placeholder="Unique username"
          hint="Admin-managed login identifier. Must be unique."
        />

        <Input
          label="Name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          placeholder="Full name"
        />

        <Input
          label="Reset Password"
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder="Leave blank to keep current password"
          hint="Min. 8 characters"
        />

        <Select
          label="Role"
          value={formData.roleId}
          onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
          required
        >
          <option value="">Select a role</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>{role.name}</option>
          ))}
        </Select>

        <div>
          <p className="block text-sm font-semibold text-slate-700 mb-2">Status</p>
          <label className="inline-flex items-center gap-3 cursor-pointer px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-slate-800">Active Account</span>
            <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${formData.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
              {formData.isActive ? 'Active' : 'Inactive'}
            </span>
          </label>
        </div>

        <Select
          label="Preferred Shift Pattern"
          value={formData.preferredPatternId}
          onChange={(e) => setFormData({ ...formData, preferredPatternId: e.target.value })}
          hint="Used as default when generating rosters."
        >
          <option value="">No preferred pattern (default: 5-on-3-off)</option>
          {patterns.map((pattern) => (
            <option key={pattern.id} value={pattern.id}>
              {pattern.name} ({pattern.daysOn}-on-{pattern.daysOff}-off)
            </option>
          ))}
        </Select>

        <div>
          <DatePicker
            label="Pattern Start Date (Anchor)"
            value={formData.patternStartDate}
            onChange={(v) => setFormData({ ...formData, patternStartDate: v })}
            placeholder="Pick anchor date"
          />
          <p className="text-xs text-slate-500 mt-1">Set to the FIRST day of a 4-day ON block for Controllers.</p>
        </div>

        <div>
          <p className="block text-sm font-semibold text-slate-700 mb-2">Skills</p>
          <div className="border border-slate-200 rounded-lg p-3 space-y-1 max-h-60 overflow-y-auto">
            {skills.map((skill) => (
              <label
                key={skill.id}
                className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={formData.skillIds.includes(skill.id)}
                  onChange={() => handleSkillToggle(skill.id)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="text-sm font-medium text-slate-800">{skill.name}</div>
                  {skill.description && (
                    <div className="text-xs text-slate-400">{skill.description}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" loading={isSaving} className="flex-1">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/admin/users')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditUser;
